/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Scrollbars } from "react-custom-scrollbars-2";

// ─── CSS ─────────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  :root {
    --sb-w: 260px;
  }

  /* ── Sidebar ── */
  .bc-sidebar {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    width: var(--sb-w);
    z-index: 1050;
    background: #fff;
    border-right: 1.5px solid #f0f0f5;
    box-shadow: 0 4px 24px rgba(123,77,18,.09);
    font-family: 'Plus Jakarta Sans', sans-serif;
    transition: transform 0.28s cubic-bezier(.4,0,.2,1);
    will-change: transform;
    display: flex; flex-direction: column;
  }

  /* Desktop: always visible, never translated */
  @media (min-width: 769px) {
    .bc-sidebar { transform: translateX(0) !important; }
  }

  /* Mobile: hidden off-screen by default */
  @media (max-width: 768px) {
    .bc-sidebar { transform: translateX(calc(-1 * var(--sb-w))); }
    .bc-sidebar.is-open { transform: translateX(0); }
  }

  /* ── Dark backdrop (mobile only) ── */
  .bc-sidebar-backdrop {
    display: none;
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1049;
    opacity: 0;
    transition: opacity 0.25s ease;
    pointer-events: none;
  }
  @media (max-width: 768px) {
    .bc-sidebar-backdrop.is-open {
      display: block;
      opacity: 1;
      pointer-events: auto;
    }
  }

  /* ── Push page content on desktop ── */
  @media (min-width: 769px) {
    .page-wrapper { margin-left: var(--sb-w) !important; }
  }
  @media (max-width: 768px) {
    .page-wrapper { margin-left: 0 !important; }
  }

  /* ── Header offset ── */
  .page-wrapper { padding-top: 60px !important; }

  /* ── Sidebar internals ── */
  .sb-logo {
    display: flex; align-items: center; gap: 10px;
    padding: 20px 20px 16px;
    border-bottom: 1.5px solid #f3f4f6;
    flex-shrink: 0;
  }
  .sb-logo-icon {
    width: 34px; height: 34px; flex-shrink: 0;
    background: linear-gradient(135deg, #7B4D12, #8B5E1A);
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 12px rgba(123,77,18,.3);
  }
  .sb-logo-icon svg { width: 17px; height: 17px; stroke: #fff; fill: none; stroke-width: 2; }
  .sb-logo-text  { font-size: 15px; font-weight: 700; color: #1a1d2e; letter-spacing: -.3px; }
  .sb-logo-badge {
    margin-left: auto; font-size: 10px; font-weight: 600;
    color: #7B4D12; background: #F5ECD7;
    border-radius: 4px; padding: 1px 6px;
  }

  .sb-search { margin: 14px 16px 6px; position: relative; flex-shrink: 0; }
  .sb-search input {
    width: 100%; background: #f7f8fc;
    border: 1.5px solid transparent; border-radius: 8px;
    padding: 8px 12px 8px 36px;
    font-family: inherit; font-size: 13px; color: #1a1d2e;
    outline: none; box-sizing: border-box;
    transition: border-color .18s, box-shadow .18s;
  }
  .sb-search input::placeholder { color: #9ca3af; }
  .sb-search input:focus { border-color: #7B4D12; box-shadow: 0 0 0 3px rgba(123,77,18,.12); background: #fff; }
  .sb-search-ico { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }

  .sb-section-label {
    font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
    color: #9ca3af; padding: 16px 20px 6px; flex-shrink: 0;
  }

  .sb-menu { list-style: none; padding: 0 10px; margin: 0; }
  .sb-menu li { margin-bottom: 2px; }

  .sb-group-btn {
    display: flex; align-items: center; gap: 10px;
    width: 100%; padding: 9px 12px; border-radius: 8px;
    background: none; border: none; cursor: pointer;
    color: #6b7280; font-family: inherit;
    font-size: 13.5px; font-weight: 500;
    transition: background .18s, color .18s;
  }
  .sb-group-btn:hover              { background: #f7f8fc; color: #1a1d2e; }
  .sb-group-btn.open,
  .sb-group-btn.has-active         { color: #1a1d2e; }

  .sb-ico-wrap {
    width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: #f7f8fc; color: #9ca3af;
    transition: background .18s, color .18s;
  }
  .sb-group-btn:hover .sb-ico-wrap,
  .sb-group-btn.open .sb-ico-wrap  { background: #F5ECD7; color: #7B4D12; }
  .sb-group-btn.has-active .sb-ico-wrap {
    background: #7B4D12; color: #fff;
    box-shadow: 0 4px 10px rgba(123,77,18,.3);
  }

  .sb-group-label { flex: 1; text-align: left; }

  .sb-arrow {
    width: 16px; height: 16px; color: #9ca3af; flex-shrink: 0;
    transition: transform .18s, color .18s;
  }
  .sb-group-btn.open .sb-arrow { transform: rotate(90deg); color: #7B4D12; }

  .sb-sub {
    list-style: none; padding: 0; margin: 0; overflow: hidden;
    max-height: 0; opacity: 0;
    transition: max-height .28s cubic-bezier(.4,0,.2,1), opacity .2s ease;
  }
  .sb-sub.open { max-height: 600px; opacity: 1; }
  .sb-sub-inner { padding: 4px 0 6px 40px; }
  .sb-sub li { margin-bottom: 1px; }

  .sb-sub-link {
    display: flex; align-items: center; gap: 8px;
    padding: 7px 10px; border-radius: 8px;
    font-size: 13px; font-weight: 400; color: #6b7280;
    text-decoration: none;
    transition: background .18s, color .18s, padding-left .18s;
  }
  .sb-sub-link::before {
    content: ''; width: 5px; height: 5px; border-radius: 50%;
    background: #9ca3af; flex-shrink: 0;
    transition: background .18s, transform .18s;
  }
  .sb-sub-link:hover { background: #f7f8fc; color: #1a1d2e; padding-left: 14px; }
  .sb-sub-link:hover::before { background: #7B4D12; }
  .sb-sub-link.active { background: #F5ECD7; color: #7B4D12; font-weight: 600; }
  .sb-sub-link.active::before { background: #7B4D12; transform: scale(1.4); }

  .sb-footer {
    margin-top: auto; padding: 14px 16px;
    border-top: 1.5px solid #f3f4f6; flex-shrink: 0;
  }
  .sb-profile {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 8px;
  }
  .sb-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 12px; font-weight: 700; flex-shrink: 0;
  }
  .sb-avatar--admin    { background: linear-gradient(135deg, #7B4D12, #8B5E1A); }
  .sb-avatar--customer { background: linear-gradient(135deg, #6B4226, #8B6340); }
  .sb-profile-info { flex: 1; min-width: 0; }
  .sb-profile-name {
    font-size: 13px; font-weight: 600; color: #1a1d2e;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .sb-role-pill {
    display: inline-flex; align-items: center;
    font-size: 10px; font-weight: 600; letter-spacing: .03em;
    padding: 2px 7px; border-radius: 20px; margin-top: 2px;
  }
  .sb-role-pill--admin    { background: #F5ECD7; color: #7B4D12; }
  .sb-role-pill--customer { background: #F5EAD5; color: #6B4226; }
  .sb-logout-btn {
    border: 0; background: transparent; color: #9ca3af;
    width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; transition: background .18s, color .18s;
  }
  .sb-logout-btn:hover { background: #F5ECD7; color: #7B4D12; }

  .sb-scroll-thumb { background: rgba(123,77,18,.15) !important; border-radius: 4px !important; }
  .sb-scroll-track { background: transparent !important; right: 2px !important; top: 2px !important; bottom: 2px !important; width: 4px !important; border-radius: 2px !important; }
`;

// ─── SVG icons ────────────────────────────────────────────────────────────────
const Ico = ({ n, s = 15 }) => {
  const d = {
    chart: (
      <>
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </>
    ),
    anchor: (
      <>
        <circle cx="12" cy="5" r="3" />
        <line x1="12" y1="22" x2="12" y2="8" />
        <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
      </>
    ),
    layers: (
      <>
        <polygon points="12 2 2 7 12 12 22 7 12 2" />
        <polyline points="2 17 12 22 22 17" />
        <polyline points="2 12 12 17 22 12" />
      </>
    ),
    cal: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </>
    ),
    tag: (
      <>
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </>
    ),
    gift: (
      <>
        <polyline points="20 12 20 22 4 22 4 12" />
        <rect x="2" y="7" width="20" height="5" />
        <line x1="12" y1="22" x2="12" y2="7" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
      </>
    ),
    card: (
      <>
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </>
    ),
    ticket: (
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
    ),
    pie: (
      <>
        <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
        <path d="M22 12A10 10 0 0 0 12 2v10z" />
      </>
    ),
    users: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </>
    ),
    chevron: <polyline points="9 18 15 12 9 6" />,
    logout: (
      <>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </>
    ),
    shield: (
      <>
        <path d="M12 2l7 4v6c0 5-5 9-7 10-2-1-7-5-7-10V6l7-4z" />
      </>
    ),
    zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  };
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {d[n] || null}
    </svg>
  );
};

// ─── Permission helper functions - Updated for granular permissions ─────────────────
const hasPermission = (userPermissions, permissionName) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;

  return userPermissions.some((p) => {
    if (typeof p === "string") {
      return p === permissionName;
    }
    return p.name === permissionName;
  });
};

const hasAnyPermission = (userPermissions, permissions) => {
  if (!userPermissions) return false;
  return permissions.some((permission) =>
    hasPermission(userPermissions, permission),
  );
};

// ─── Menu visibility conditions based on granular CRUD permissions ─────────────────────────
const getMenuConfig = (userPermissions) => {
  const allMenus = [
    {
      key: "dashboard",
      ico: "chart",
      title: "Dashboard",
      condition: () => hasPermission(userPermissions, "reports.view"),
      items: [
        {
          label: "Overview",
          to: "/dashboard",
          condition: () => hasPermission(userPermissions, "reports.view"),
        },
      ],
    },
    {
      key: "reports",
      ico: "pie",
      title: "Reports",
      condition: () => hasPermission(userPermissions, "reports.view"),
      items: [
        {
          label: "Summary",
          to: "/reports",
          condition: () => hasPermission(userPermissions, "reports.view"),
        },
      ],
    },
    {
      key: "roles",
      ico: "shield",
      title: "Roles & Permissions",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "roles.view",
          "roles.create",
          "roles.edit",
          "roles.delete",
        ]),
      items: [
        {
          label: "All Roles",
          to: "/roles",
          condition: () => hasPermission(userPermissions, "roles.view"),
        },
        {
          label: "Add Role",
          to: "/roles/add",
          condition: () => hasPermission(userPermissions, "roles.create"),
        },
      ],
    },
    {
      key: "admin_users",
      ico: "users",
      title: "Admin Management",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "users.view",
          "users.create",
          "users.edit",
          "users.delete",
        ]),
      items: [
        {
          label: "Admin Users",
          to: "/users/admins",
          condition: () => hasPermission(userPermissions, "users.view"),
        },
        {
          label: "Add Admin",
          to: "/users/add-admin",
          condition: () => hasPermission(userPermissions, "users.create"),
        },
      ],
    },
    {
      key: "customers",
      ico: "users",
      title: "Customer Management",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "users.view",
          "users.create",
          "users.edit",
          "users.delete",
        ]),
      items: [
        {
          label: "All Customers",
          to: "/users/customers",
          condition: () => hasPermission(userPermissions, "users.view"),
        },
        {
          label: "Add Customer",
          to: "/users/add-customer",
          condition: () => hasPermission(userPermissions, "users.create"),
        },
      ],
    },
    {
      key: "boat",
      ico: "anchor",
      title: "Boat & Decks",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "boat.view",
          "boat.create",
          "boat.edit",
          "boat.delete",
        ]),
      items: [
        {
          label: "All Decks",
          to: "/boat/decks",
          condition: () => hasPermission(userPermissions, "boat.view"),
        },
      ],
    },
    {
      key: "trips",
      ico: "layers",
      title: "Trips",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "trips.view",
          "trips.create",
          "trips.edit",
          "trips.delete",
        ]),
      items: [
        {
          label: "All Trips",
          to: "/trips",
          condition: () => hasPermission(userPermissions, "trips.view"),
        },
        {
          label: "Add Trip",
          to: "/trips/add",
          condition: () => hasPermission(userPermissions, "trips.create"),
        },
      ],
    },
    // {
    //   key: "schedules",
    //   ico: "cal",
    //   title: "Schedules",
    //   condition: () =>
    //     hasAnyPermission(userPermissions, [
    //       "schedules.view",
    //       "schedules.create",
    //       "schedules.edit",
    //       "schedules.delete",
    //     ]),
    //   items: [
    //     {
    //       label: "All Schedules",
    //       to: "/schedules",
    //       condition: () => hasPermission(userPermissions, "schedules.view"),
    //     },
    //   ],
    // },
    {
      key: "pricing",
      ico: "tag",
      title: "Pricing",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "pricing.view",
          "pricing.create",
          "pricing.edit",
          "pricing.delete",
        ]),
      items: [
        {
          label: "Pricing Management",
          to: "/pricing",
          condition: () => hasPermission(userPermissions, "pricing.view"),
        },
      ],
    },

    {
      key: "promo_codes",
      ico: "gift",
      title: "Promo Codes",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "promocodes.view",
          "promocodes.create",
          "promocodes.edit",
          "promocodes.delete",
        ]),
      items: [
        {
          label: "All Promo Codes",
          to: "/promo-codes",
          condition: () => hasPermission(userPermissions, "promocodes.view"),
        },
        {
          label: "Generate Codes",
          to: "/promo-codes/generate",
          condition: () => hasPermission(userPermissions, "promocodes.create"),
        },
      ],
    },
    {
      key: "reservations",
      ico: "ticket",
      title: "Reservations",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "reservations.view",
          "reservations.create",
          "reservations.edit",
          "reservations.refund",
          "reservations.delete",
        ]),
      items: [
        {
          label: "All Reservations",
          to: "/reservations",
          condition: () => hasPermission(userPermissions, "reservations.view"),
        },
        // {
        //   label: "My Reservations",
        //   to: "/reservations/my",
        //   condition: () => true, // All authenticated users
        // },
        {
          label: "New Reservation",
          to: "/reservations/add",
          condition: () =>
            hasPermission(userPermissions, "reservations.create"),
        },
      ],
    },
    {
      key: "refunds",
      ico: "zap",
      title: "Refunds",
      condition: () =>
        hasAnyPermission(userPermissions, ["reservations.refund"]),
      items: [
        {
          label: "All Refunds",
          to: "/refunds",
          condition: () => hasPermission(userPermissions, "reservations.refund"),
        },
      ],
    },
    {
      key: "payments",
      ico: "card",
      title: "Payments",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "payments.view",
          "payments.create",
          "payments.edit",
          "payments.delete",
        ]),
      items: [
        {
          label: "All Payments",
          to: "/payments",
          condition: () => hasPermission(userPermissions, "payments.view"),
        },
      ],
    },
    {
      key: "tickets",
      ico: "tag",
      title: "Tickets",
      condition: () =>
        hasAnyPermission(userPermissions, [
          "tickets.view",
          "tickets.self_view",
          "tickets.check_in",
        ]),
      items: [
        {
          label: "All Tickets",
          to: "/tickets",
          condition: () => hasPermission(userPermissions, "tickets.view"),
        },
        // {
        //   label: "My Tickets",
        //   to: "/tickets/my",
        //   condition: () => true, // All authenticated users
        // },
        {
          label: "Scan QR",
          to: "/tickets/scan",
          condition: () => hasPermission(userPermissions, "tickets.check_in"),
        },
      ],
    },
    {
      key: "requests",
      ico: "tag",
      title: "Booking Requests",
      condition: () => hasPermission(userPermissions, "requests.manage"),
      items: [
        {
          label: "Requests Queue",
          to: "/requests",
          condition: () => hasPermission(userPermissions, "requests.manage"),
        },
      ],
    },
    {
      key: "audit",
      ico: "shield",
      title: "Audit Log",
      condition: () => hasPermission(userPermissions, "audit.view"),
      items: [
        {
          label: "Activity Log",
          to: "/audit-logs",
          condition: () => hasPermission(userPermissions, "audit.view"),
        },
      ],
    },
  ];

  // Filter menus based on conditions
  const filteredMenus = allMenus
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.condition ? item.condition() : false,
      ),
    }))
    .filter((group) =>
      group.condition ? group.condition() : group.items.length > 0,
    );

  return filteredMenus;
};

// ══════════════════════════════════════════════════════════════════════════════
const Sidebar = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const [openKey, setOpenKey] = useState(null);
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  // ── Load user data from localStorage ─────────────────────────────────────
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        const userStr = localStorage.getItem("user");

        if (!userStr) {
          setLoading(false);
          return;
        }

        const userData = JSON.parse(userStr);
        setUser(userData);

        // Extract permissions - handles both string array and object array
        let permissions = [];

        if (userData.permissions && Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
          console.log("📋 Loaded permissions:", permissions);
        } else if (userData.role && userData.role.permissions) {
          permissions = userData.role.permissions;
          console.log("📋 Loaded role permissions:", permissions);
        }

        setUserPermissions(permissions);
      } catch (error) {
        console.error("Error loading user data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  // ── Listen for the custom event fired by Header's hamburger ──────────────
  useEffect(() => {
    const toggleSidebar = () => {
      setMobileOpen((prev) => !prev);
    };

    window.addEventListener("bc-toggle-sidebar", toggleSidebar);

    return () => {
      window.removeEventListener("bc-toggle-sidebar", toggleSidebar);
    };
  }, []);

  // ── Close on route change (after navigation) ─────────────────────────────
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // ── Prevent body scroll when sidebar open on mobile ──────────────────────
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile && mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Get dynamic menu based on user permissions
  const menuConfig = useMemo(() => {
    const config = getMenuConfig(userPermissions);
    console.log(
      "📋 Generated menu config:",
      config.map((g) => ({
        title: g.title,
        itemsCount: g.items.length,
        items: g.items.map((i) => i.label),
      })),
    );
    return config;
  }, [userPermissions]);

  const displayName = user
    ? user.firstName || user.lastName
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : user.email || "User"
    : "User";
  const initials =
    displayName
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";
  const roleLabel = useMemo(() => {
    const r = user?.role;
    if (!r) return null;
    const n = typeof r === "string" ? r : r?.name || "";
    return n ? n.charAt(0).toUpperCase() + n.slice(1).toLowerCase() : null;
  }, [user]);

  // Filter menu based on search
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return menuConfig;
    return menuConfig
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) =>
            i.label.toLowerCase().includes(q) ||
            g.title.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.items.length);
  }, [menuConfig, search]);

  // Auto-open active group on route change
  useEffect(() => {
    const activeGroup = menuConfig.find((g) =>
      g.items.some((i) => pathname.startsWith(i.to)),
    );
    setOpenKey(activeGroup?.key || null);
  }, [pathname, menuConfig]);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    history.replace("/login");
  };

  // Show loading state
  if (loading) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="bc-sidebar">
          <div
            style={{ padding: "20px", textAlign: "center", marginTop: "100px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading menu...</p>
          </div>
        </div>
      </>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{STYLES}</style>

      {/* Mobile backdrop */}
      <div
        className={`bc-sidebar-backdrop${mobileOpen ? " is-open" : ""}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Sidebar panel */}
      <div id="sidebar" className={`bc-sidebar${mobileOpen ? " is-open" : ""}`}>
        <Scrollbars
          autoHide
          autoHideTimeout={1000}
          autoHideDuration={200}
          renderThumbVertical={({ style, ...p }) => (
            <div style={style} className="sb-scroll-thumb" {...p} />
          )}
          renderTrackVertical={({ style, ...p }) => (
            <div style={style} className="sb-scroll-track" {...p} />
          )}
          renderView={({ style, ...p }) => (
            <div style={{ ...style, overflowX: "hidden" }} {...p} />
          )}
          universal
          style={{ height: "100%", width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100%",
            }}
          >
            {/* Logo */}
            <div className="sb-logo">
              <div className="bc-logo">
                <img
                  src="/EF-logo-dark.png"
                  alt="EF Logo"
                  style={{ width: "150px", height: "auto" }}
                />
              </div>
              <span className="sb-logo-badge">Admin</span>
            </div>

            {/* Search */}
            <div className="sb-search">
              <span className="sb-search-ico">
                <Ico n="search" s={14} />
              </span>
              <input
                type="text"
                placeholder="Search menu…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Nav */}
            <div style={{ flex: 1, paddingBottom: 8 }}>
              {!search && filtered.length > 0 && (
                <div className="sb-section-label">Navigation</div>
              )}
              <ul className="sb-menu">
                {filtered.map((group) => {
                  const isOpen = openKey === group.key;
                  const hasActive = group.items.some((i) =>
                    pathname.startsWith(i.to),
                  );
                  return (
                    <li key={group.key}>
                      <button
                        className={`sb-group-btn${isOpen ? " open" : ""}${
                          hasActive ? " has-active" : ""
                        }`}
                        onClick={() =>
                          setOpenKey((prev) =>
                            prev === group.key ? null : group.key,
                          )
                        }
                      >
                        <span className="sb-ico-wrap">
                          <Ico n={group.ico} s={15} />
                        </span>
                        <span className="sb-group-label">{group.title}</span>
                        <span className="sb-arrow">
                          <Ico n="chevron" s={14} />
                        </span>
                      </button>

                      <ul className={`sb-sub${isOpen ? " open" : ""}`}>
                        <div className="sb-sub-inner">
                          {group.items.map((item) => (
                            <li key={item.to}>
                              <Link
                                to={item.to}
                                className={`sb-sub-link${
                                  pathname.startsWith(item.to) ? " active" : ""
                                }`}
                              >
                                {item.label}
                              </Link>
                            </li>
                          ))}
                        </div>
                      </ul>
                    </li>
                  );
                })}
              </ul>
              {filtered.length === 0 && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#9ca3af",
                  }}
                >
                  No menu items available
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sb-footer">
              <div className="sb-profile">
                <div className={`sb-avatar sb-avatar--admin`}>{initials}</div>
                <div className="sb-profile-info">
                  <div className="sb-profile-name">{displayName}</div>
                  {roleLabel && (
                    <span className={`sb-role-pill sb-role-pill--admin`}>
                      {roleLabel}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="sb-logout-btn"
                  onClick={handleLogout}
                  title="Sign out"
                >
                  <Ico n="logout" s={14} />
                </button>
              </div>
            </div>
          </div>
        </Scrollbars>
      </div>
    </>
  );
};

export default Sidebar;
